/**
 * Which model reads the question, when the student has an opinion.
 *
 * Region was the only lever here before, and it was an indirect one: you told
 * the app where you were and it inferred a provider from that. That works for
 * the default case and is the wrong shape for every deliberate one - a class
 * comparing the two, an instructor whose network geolocates somewhere unhelpful,
 * a student who would simply rather their words went to one company than the
 * other. All three of those are asking about a PROVIDER, and were having to
 * express it as a country.
 *
 * So the choice is now stated directly, with three values:
 *
 *   auto      - decide from region, exactly as before. Still the default.
 *   openai    - always ChatGPT, wherever the request comes from.
 *   deepseek  - always DeepSeek.
 *
 * `auto` is not a fourth provider; it is the absence of a choice, and it leaves
 * `region.ts` in charge. Keeping it as a distinct value rather than writing the
 * resolved provider into storage matters: a student who picked "auto" in one
 * country should still get the local default after they travel, and storing
 * `openai` would silently follow them.
 *
 * WORTH KNOWING, and the reason this is a safe thing to hand a student: the
 * hosted model only ever returns an intent id and any knob values the question
 * named. Choosing the "wrong" provider changes who picked the experiment, never
 * what the experiment computed - the numbers come from the solver either way.
 */

import { PROVIDER_FOR_REGION, type ProviderId } from './config'
import { currentRegion } from './region'

/** A stated provider, or `auto` to let region decide. */
export type ProviderChoice = ProviderId | 'auto'

const STORAGE_KEY = 'simfab.assistant.provider'

/** A remembered choice, or null if the student has never made one. */
export function storedProvider(): ProviderId | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'openai' || raw === 'deepseek' ? raw : null
  } catch {
    // Private browsing, or storage disabled by policy. Fall through to auto.
    return null
  }
}

export function setProvider(provider: ProviderId | null): void {
  try {
    if (provider) localStorage.setItem(STORAGE_KEY, provider)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do - the choice still applies to this session, it just will
    // not outlive the tab.
  }
}

/** The choice as the UI should show it, with `auto` when none was made. */
export const providerChoice = (): ProviderChoice => storedProvider() ?? 'auto'

/**
 * Which provider the client believes will answer.
 *
 * A label rather than a decision: the proxy resolves this again on its own side
 * because only it can see the edge country header, and only it knows whether
 * the key for a provider is actually configured. This is what to print before
 * the first question comes back.
 */
export const currentProvider = (): ProviderId =>
  storedProvider() ?? PROVIDER_FOR_REGION[currentRegion()]
